import { useState, type ReactNode } from 'react';
import {
  EMPLOYMENT_TYPES,
  toMinorUnits,
  WORK_MODES,
  type CurrencyCode,
  type EmploymentType,
  type WorkMode,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Field, FieldRow } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { Modal } from '../../../components/Modal';
import { Select } from '../../../components/Select';
import { errorMessage } from '../../../lib/api-client';
import { useIsFieldHidden } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useCreatePosition, useDepartments, useTeams, useVentures } from '../api/organisation-api';
import { useCurrencyOptions, withCurrent } from '../../../lib/app-settings';
import styles from './NewPositionDialog.module.css';

/**
 * F3.02 — add a budgeted seat.
 *
 * **No code field.** The server generates `{VENTURE}-{DEPT}-{SEQ}`, so there is
 * nothing for anyone to get wrong and no way for two people to claim one number.
 * The form says what the code will look like rather than asking for it.
 *
 * Amounts are typed in whole taka or pounds and converted to minor units here.
 * Nobody types poisha, and a form that asked for them would collect a number two
 * decimal places wrong roughly every time.
 */

const TYPE_LABELS: Readonly<Record<EmploymentType, string>> = {
  full_time: 'Full time',
  part_time: 'Part time',
  contract: 'Contract',
  intern: 'Intern',
};

const MODE_LABELS: Readonly<Record<WorkMode, string>> = {
  onsite: 'On site',
  hybrid: 'Hybrid',
  remote: 'Remote',
};

export function NewPositionDialog({ onClose }: { readonly onClose: () => void }): ReactNode {
  const currencyOptions = useCurrencyOptions();
  const showToast = useToasts((state) => state.show);
  const isBandHidden = useIsFieldHidden('position.salaryBand');

  const ventures = useVentures();
  const departments = useDepartments();
  const teams = useTeams();
  const create = useCreatePosition();

  const [ventureId, setVentureId] = useState<string | null>(null);
  const [departmentId, setDepartmentId] = useState<string | null>(null);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [employmentType, setEmploymentType] = useState<EmploymentType>('full_time');
  const [workMode, setWorkMode] = useState<WorkMode>('onsite');
  const [location, setLocation] = useState('');
  const [totalSlots, setTotalSlots] = useState('1');
  const [status, setStatus] = useState<'vacant' | 'planned'>('vacant');
  const [bandMin, setBandMin] = useState('');
  const [bandMax, setBandMax] = useState('');
  const [currency, setCurrency] = useState<CurrencyCode | null>(null);
  const [error, setError] = useState<string | null>(null);

  const department = (departments.data ?? []).find((d) => d.id === departmentId);
  const venture = (ventures.data ?? []).find((v) => v.id === ventureId);
  // Department names repeat across ventures (every venture can have a Marketing),
  // so the venture is chosen first and the list only offers its departments.
  const ventureDepartments = (departments.data ?? []).filter((d) => d.ventureId === ventureId);
  const availableTeams = (teams.data ?? []).filter((t) => t.departmentId === departmentId);

  // The currency defaults to the venture's, which is what D13 says a position
  // inherits when nobody chooses one.
  const effectiveCurrency = currency ?? (venture?.defaultCurrency as CurrencyCode | undefined) ?? null;

  const codePreview =
    venture && department?.code ? `${venture.code}-${department.code}-###` : 'Chosen automatically';

  async function submit(): Promise<void> {
    setError(null);
    if (!departmentId) return setError('Choose the department this seat belongs to.');

    const slots = Number.parseInt(totalSlots, 10);
    if (!Number.isFinite(slots) || slots < 1 || slots > 50) {
      return setError('Headcount must be between 1 and 50.');
    }

    try {
      const created = await create.mutateAsync({
        departmentId,
        ...(teamId ? { teamId } : {}),
        title: title.trim(),
        employmentType,
        workMode,
        ...(location.trim() ? { location: location.trim() } : {}),
        totalSlots: slots,
        status,
        ...bandFields(bandMin, bandMax, effectiveCurrency),
      });
      showToast({ tone: 'success', message: `${created.code} added`, detail: created.title });
      onClose();
    } catch (cause) {
      setError(errorMessage(cause));
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Add a position"
      description="A position is one budgeted seat. It exists whether somebody is in it or not."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="solid"
            isPending={create.isPending}
            disabled={title.trim() === '' || departmentId === null}
            onClick={() => void submit()}
          >
            Add it
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <Field label="Venture" isRequired>
          {({ inputId }) => (
            <Select
              id={inputId}
              label="Venture"
              placeholder="Choose a venture"
              options={(ventures.data ?? []).map((v) => ({ value: v.id, label: v.name }))}
              value={ventureId}
              onChange={(value) => {
                setVentureId(value);
                setDepartmentId(null);
                setTeamId(null);
              }}
            />
          )}
        </Field>

        <Field label="Department" isRequired hint={`Code will be ${codePreview}`}>
          {({ inputId }) => (
            <Select
              id={inputId}
              label="Department"
              placeholder={ventureId ? 'Choose a department' : 'Choose a venture first'}
              disabled={ventureId === null}
              options={ventureDepartments.map((d) => ({ value: d.id, label: d.name }))}
              value={departmentId}
              onChange={(value) => {
                setDepartmentId(value);
                setTeamId(null);
              }}
            />
          )}
        </Field>

        {availableTeams.length > 0 && (
          <Field label="Team" isOptional>
            {({ inputId }) => (
              <Select
                id={inputId}
                label="Team"
                placeholder="No team"
                options={availableTeams.map((t) => ({ value: t.id, label: t.name }))}
                value={teamId}
                onChange={setTeamId}
              />
            )}
          </Field>
        )}

        <Field label="Title" isRequired>
          {({ inputId, isInvalid }) => (
            <Input
              id={inputId}
              isInvalid={isInvalid}
              value={title}
              autoFocus
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Senior accountant"
            />
          )}
        </Field>

        <FieldRow>
          <Field label="Seats" hint="How many people this position budgets for.">
            {({ inputId }) => (
              <Input
                id={inputId}
                value={totalSlots}
                isNumeric
                inputMode="numeric"
                onChange={(event) => setTotalSlots(event.target.value)}
              />
            )}
          </Field>
        </FieldRow>

        <FieldRow>
          <Field label="Employment type">
            {({ inputId }) => (
              <Select
                id={inputId}
                label="Employment type"
                options={EMPLOYMENT_TYPES.map((value) => ({ value, label: TYPE_LABELS[value] }))}
                value={employmentType}
                onChange={(value) => setEmploymentType(value as EmploymentType)}
              />
            )}
          </Field>
          <Field label="Work mode">
            {({ inputId }) => (
              <Select
                id={inputId}
                label="Work mode"
                options={WORK_MODES.map((value) => ({ value, label: MODE_LABELS[value] }))}
                value={workMode}
                onChange={(value) => setWorkMode(value as WorkMode)}
              />
            )}
          </Field>
        </FieldRow>

        <Field label="Location" isOptional>
          {({ inputId }) => (
            <Input
              id={inputId}
              value={location}
              onChange={(event) => setLocation(event.target.value)}
              placeholder="Dhaka"
            />
          )}
        </Field>

        <Field
          label="Status"
          hint="A planned seat is a future need that has not been budgeted yet. It stays out of live recruitment counts."
        >
          {({ inputId }) => (
            <Select
              id={inputId}
              label="Status"
              options={[
                { value: 'vacant', label: 'Vacant — budgeted and open' },
                { value: 'planned', label: 'Planned — a future need' },
              ]}
              value={status}
              onChange={(value) => setStatus(value as 'vacant' | 'planned')}
            />
          )}
        </Field>

        {!isBandHidden && (
          <FieldRow>
            <Field label="Band minimum" isOptional>
              {({ inputId }) => (
                <Input
                  id={inputId}
                  value={bandMin}
                  isNumeric
                  inputMode="numeric"
                  prefix={effectiveCurrency ?? undefined}
                  onChange={(event) => setBandMin(event.target.value)}
                  placeholder="65000"
                />
              )}
            </Field>
            <Field label="Band maximum" isOptional>
              {({ inputId }) => (
                <Input
                  id={inputId}
                  value={bandMax}
                  isNumeric
                  inputMode="numeric"
                  prefix={effectiveCurrency ?? undefined}
                  onChange={(event) => setBandMax(event.target.value)}
                  placeholder="90000"
                />
              )}
            </Field>
            <Field label="Currency" hint="Inherited from the venture unless you change it.">
              {({ inputId }) => (
                <Select
                  id={inputId}
                  label="Currency"
                  options={withCurrent(currencyOptions, effectiveCurrency)}
                  value={effectiveCurrency}
                  onChange={(value) => setCurrency(value as CurrencyCode)}
                />
              )}
            </Field>
          </FieldRow>
        )}

        {error !== null && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

/**
 * Whole units in, minor units out — as a digit string, because JSON has no bigint
 * and a float would lose precision on a lakh of poisha (non-negotiable #7).
 */
function bandFields(
  min: string,
  max: string,
  currency: CurrencyCode | null,
): {
  salaryBandMinMinor?: string;
  salaryBandMaxMinor?: string;
  salaryCurrency?: CurrencyCode;
} {
  const parsedMin = parseAmount(min);
  const parsedMax = parseAmount(max);
  if (parsedMin === null && parsedMax === null) return {};
  if (!currency) return {};

  return {
    ...(parsedMin === null ? {} : { salaryBandMinMinor: toMinorUnits(parsedMin, currency).toString() }),
    ...(parsedMax === null ? {} : { salaryBandMaxMinor: toMinorUnits(parsedMax, currency).toString() }),
    salaryCurrency: currency,
  };
}

function parseAmount(input: string): number | null {
  const cleaned = input.replace(/[\s,]/g, '');
  if (cleaned === '') return null;
  const value = Number(cleaned);
  return Number.isFinite(value) && value >= 0 ? value : null;
}
